import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Col, Form, Row, Tab, Tabs } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Get } from '../../utils/httpProvider';
import { accountAuthInfoPagingAction } from '../../store/reducers/account';
import {
    ACCOUNTAUTHINFO_API,
    WORK_RESULT_API,
    POSITION_API,
    JOBGROUP_API,
    ANNUAL_JOB_API
} from '../../utils/apiUrl';

const fmt = value => value === null || value === undefined ? '-' : Number(value).toFixed(2);
const fmtDate = value => value ? new Date(value).toLocaleString('vi-VN') : '-';
const normalize = value => (value || '').toString().trim().toLocaleLowerCase('vi-VN');

const isGuid = str => {
    if (!str || typeof str !== 'string') return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str.trim());
};

const toInputDate = value => {
    const date = new Date(value);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
};

const getDefaultToDate = () => toInputDate(new Date());
const getDefaultFromDate = () => {
    const today = new Date();
    const lastDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    const date = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        Math.min(today.getDate(), lastDayOfPreviousMonth)
    );
    return toInputDate(date);
};

const getPagedData = response => {
    const data = response?.data ?? response?.Data ?? [];
    return Array.isArray(data) ? data : (data?.$values || []);
};

const getArrayData = response => {
    const data = response?.data ?? response?.Data ?? response ?? [];
    return Array.isArray(data) ? data : (data?.$values || []);
};

const getFullName = accountInfo => {
    if (!accountInfo) return '';
    const auth = accountInfo.accountAuthInfo || accountInfo.AccountAuthInfo || accountInfo;
    const lastName = auth.lastName || auth.LastName || '';
    const middleName = auth.middleName || auth.MiddleName || '';
    const firstName = auth.firstName || auth.FirstName || '';
    const fullName = `${lastName} ${middleName} ${firstName}`.trim().replace(/\s+/g, ' ');
    if (fullName) return fullName;
    const directFullName = accountInfo.fullName || accountInfo.FullName;
    if (directFullName && typeof directFullName === 'string' && !isGuid(directFullName)) {
        return directFullName.trim();
    }
    return '';
};

const getAccountUsername = acc => {
    if (!acc) return '';
    const candidates = [
        acc.username,
        acc.Username,
        acc.userName,
        acc.UserName,
        acc.account?.username,
        acc.Account?.username,
        acc.account?.Username,
        acc.Account?.Username,
        acc.accountResponse?.username,
        acc.AccountResponse?.username,
        acc.accountResponse?.Username,
        acc.AccountResponse?.Username
    ];
    for (const u of candidates) {
        if (u && typeof u === 'string' && !isGuid(u)) {
            return u.trim();
        }
    }
    return '';
};

const getMemberOptionLabel = acc => {
    const fullName = getFullName(acc);
    const username = getAccountUsername(acc);
    const isNumericOrId = !username || isGuid(username) || /^\d+$/.test(username);

    if (fullName && !isNumericOrId) {
        return `${fullName} (${username})`;
    }
    if (fullName) {
        return fullName;
    }
    if (username && !isGuid(username)) {
        return username;
    }
    return acc?.email || 'Chưa đặt tên';
};

const getErrorMessage = error => {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    const validationErrors = error?.errors;
    if (validationErrors) {
        const firstError = Object.values(validationErrors).flat()[0];
        if (firstError) return firstError;
    }
    return 'Không thể xuất dữ liệu.';
};

const getWorkTypeName = (workType, projectType) => {
    if (workType === 1) return 'Thường niên';
    if (projectType === 1) return 'Giao việc';
    if (projectType === 0) return 'Đột xuất';
    return 'Dự án / Giao việc';
};

const getStatusName = (status, workType) => {
    switch (Number(status)) {
        case 0:
            return workType === 1 ? 'Chưa thực hiện' : 'Cần làm';
        case 1:
            return 'Đang làm';
        case 3:
            return 'Hoàn thành';
        case 4:
            return workType === 1 ? 'Quá hạn' : 'Không hoàn thành';
        default:
            return `Trạng thái ${status}`;
    }
};

const selectCustomStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        height: '38px',
        fontSize: '14px',
        borderRadius: '4px',
        borderColor: state.isFocused ? '#80bdff' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : null,
        backgroundColor: '#fff'
    }),
    valueContainer: (base) => ({
        ...base,
        height: '38px',
        padding: '0 8px'
    }),
    input: (base) => ({
        ...base,
        margin: '0px'
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: '38px'
    }),
    menu: (base) => ({
        ...base,
        fontSize: '14px',
        zIndex: 9999
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999
    })
};

const ALL_TASK_COLUMNS = [
    { id: 'stt', label: 'STT', defaultWidth: 6, group: 'Thông tin chung' },
    { id: 'username', label: 'Tài khoản (Username)', defaultWidth: 15, group: 'Nhân sự' },
    { id: 'fullName', label: 'Họ và tên', defaultWidth: 24, group: 'Nhân sự' },
    { id: 'position', label: 'Vị trí / Chức vụ', defaultWidth: 20, group: 'Nhân sự' },
    { id: 'workType', label: 'Loại công việc', defaultWidth: 16, group: 'Công việc' },
    { id: 'jobGroup', label: 'Nhóm công việc', defaultWidth: 22, group: 'Công việc' },
    { id: 'category', label: 'Danh mục công việc', defaultWidth: 25, group: 'Công việc' },
    { id: 'workName', label: 'Tên công việc', defaultWidth: 35, group: 'Công việc' },
    { id: 'description', label: 'Mô tả / Yêu cầu', defaultWidth: 30, group: 'Công việc' },
    { id: 'status', label: 'Trạng thái', defaultWidth: 16, group: 'Tiến độ & Trạng thái' },
    { id: 'returned', label: 'Trạng thái hoàn trả', defaultWidth: 15, group: 'Tiến độ & Trạng thái' },
    { id: 'returnComment', label: 'Lý do hoàn trả', defaultWidth: 30, group: 'Tiến độ & Trạng thái' },
    { id: 'registeredKPI', label: 'Khối lượng ĐK', defaultWidth: 14, group: 'Khối lượng (Thường niên)' },
    { id: 'currentKPI', label: 'Khối lượng đạt', defaultWidth: 15, group: 'Khối lượng (Thường niên)' },
    { id: 'unit', label: 'Đơn vị tính', defaultWidth: 12, group: 'Khối lượng (Thường niên)' },
    { id: 'createdDate', label: 'Ngày giao / Tạo', defaultWidth: 18, group: 'Thời gian thực hiện' },
    { id: 'startDate', label: 'Ngày bắt đầu', defaultWidth: 18, group: 'Thời gian thực hiện' },
    { id: 'endDate', label: 'Hạn hoàn thành', defaultWidth: 18, group: 'Thời gian thực hiện' },
    { id: 'actualEndDate', label: 'Ngày HT thực tế', defaultWidth: 18, group: 'Thời gian thực hiện' },
    { id: 'baseScore', label: 'Điểm cơ sở / Trọng số', defaultWidth: 18, group: 'Điểm số & Đánh giá' },
    { id: 'evaluationPercent', label: 'Tỷ lệ đánh giá', defaultWidth: 15, group: 'Điểm số & Đánh giá' },
    { id: 'actualScore', label: 'Điểm thực nhận', defaultWidth: 15, group: 'Điểm số & Đánh giá' },
    { id: 'evaluatedBy', label: 'Người đánh giá', defaultWidth: 18, group: 'Điểm số & Đánh giá' },
    { id: 'evaluatedAt', label: 'Ngày đánh giá', defaultWidth: 18, group: 'Điểm số & Đánh giá' },
    { id: 'evaluationNote', label: 'Ghi chú đánh giá', defaultWidth: 30, group: 'Điểm số & Đánh giá' }
];

const EVAL_COLUMNS = [
    { id: 'stt', label: 'STT', defaultWidth: 7, group: 'Chung' },
    { id: 'username', label: 'Tài khoản', defaultWidth: 16, group: 'Nhân sự' },
    { id: 'fullName', label: 'Họ và tên', defaultWidth: 25, group: 'Nhân sự' },
    { id: 'position', label: 'Vị trí / Chức vụ', defaultWidth: 20, group: 'Nhân sự' },
    { id: 'workType', label: 'Loại công việc', defaultWidth: 18, group: 'Công việc' },
    { id: 'jobGroup', label: 'Nhóm công việc', defaultWidth: 22, group: 'Công việc' },
    { id: 'category', label: 'Danh mục công việc', defaultWidth: 25, group: 'Công việc' },
    { id: 'workName', label: 'Tên công việc', defaultWidth: 36, group: 'Công việc' },
    { id: 'status', label: 'Trạng thái', defaultWidth: 16, group: 'Tiến độ' },
    { id: 'endDate', label: 'Hạn hoàn thành', defaultWidth: 21, group: 'Thời gian' },
    { id: 'baseScore', label: 'Điểm trước đánh giá', defaultWidth: 20, group: 'Đánh giá' },
    { id: 'evaluationPercent', label: 'Tỷ lệ đánh giá', defaultWidth: 16, group: 'Đánh giá' },
    { id: 'actualScore', label: 'Điểm thực nhận', defaultWidth: 18, group: 'Đánh giá' },
    { id: 'evaluatedBy', label: 'Đánh giá gần nhất', defaultWidth: 32, group: 'Đánh giá' }
];

const ALL_TASKS_WORK_TYPE_OPTIONS = [
    { value: '', label: '-- Tất cả loại công việc --' },
    { value: 'annual', label: 'Công việc thường niên' },
    { value: 'assigned', label: 'Giao việc' },
    { value: 'extraordinary', label: 'Công việc đột xuất' }
];

const EVAL_WORK_TYPE_OPTIONS = [
    { value: '', label: '-- Tất cả loại công việc --' },
    { value: '1', label: 'Công việc thường niên' },
    { value: '2', label: 'Dự án / Giao việc' }
];

const ALL_TASKS_STATUS_OPTIONS = [
    { value: '', label: '-- Tất cả trạng thái --' },
    { value: '0', label: 'Chưa thực hiện / Cần làm' },
    { value: '1', label: 'Đang làm' },
    { value: '3', label: 'Hoàn thành' },
    { value: '4', label: 'Quá hạn / Chưa hoàn thành' }
];

const EVAL_STATUS_OPTIONS = [
    { value: '', label: '-- Tất cả trạng thái --' },
    { value: '3', label: 'Hoàn thành' },
    { value: '4', label: 'Quá hạn' }
];

const EVALUATED_OPTIONS = [
    { value: '', label: '-- Tất cả --' },
    { value: 'true', label: 'Đã đánh giá' },
    { value: 'false', label: 'Chưa đánh giá' }
];

const RETURNED_OPTIONS = [
    { value: '', label: '-- Tất cả --' },
    { value: 'false', label: 'Chưa hoàn trả' },
    { value: 'true', label: 'Bị hoàn trả' }
];

const ReportExport = () => {
    const dispatch = useDispatch();
    const { listAccount } = useSelector((state) => state.account);
    const [fetchedAccounts, setFetchedAccounts] = useState([]);
    const [positions, setPositions] = useState([]);
    const [jobGroups, setJobGroups] = useState([]);
    const [annualJobs, setAnnualJobs] = useState([]);

    // Tab 1: Toàn bộ công việc nhân viên
    const [allTasksFromDate, setAllTasksFromDate] = useState(getDefaultFromDate);
    const [allTasksToDate, setAllTasksToDate] = useState(getDefaultToDate);
    const [allTasksPositionId, setAllTasksPositionId] = useState('');
    const [allTasksUserId, setAllTasksUserId] = useState('');
    const [allTasksJobGroupId, setAllTasksJobGroupId] = useState('');
    const [allTasksCategoryId, setAllTasksCategoryId] = useState('');
    const [allTasksWorkTypeCategory, setAllTasksWorkTypeCategory] = useState('');
    const [allTasksStatus, setAllTasksStatus] = useState('');
    const [allTasksEvaluated, setAllTasksEvaluated] = useState('');
    const [allTasksReturned, setAllTasksReturned] = useState('');
    const [allTasksColumns, setAllTasksColumns] = useState(ALL_TASK_COLUMNS.map(c => c.id));
    const [showAllTasksColConfig, setShowAllTasksColConfig] = useState(false);
    const [isExportingAllTasks, setIsExportingAllTasks] = useState(false);

    // Tab 2: Đánh giá kết quả
    const [evalFromDate, setEvalFromDate] = useState(getDefaultFromDate);
    const [evalToDate, setEvalToDate] = useState(getDefaultToDate);
    const [evalPositionId, setEvalPositionId] = useState('');
    const [evalUserId, setEvalUserId] = useState('');
    const [evalJobGroupId, setEvalJobGroupId] = useState('');
    const [evalCategoryId, setEvalCategoryId] = useState('');
    const [evalWorkType, setEvalWorkType] = useState('');
    const [evalStatus, setEvalStatus] = useState('');
    const [evalEvaluated, setEvalEvaluated] = useState('');
    const [evalReturned, setEvalReturned] = useState('');
    const [evalColumns, setEvalColumns] = useState(EVAL_COLUMNS.map(c => c.id));
    const [showEvalColConfig, setShowEvalColConfig] = useState(false);
    const [isExportingEval, setIsExportingEval] = useState(false);

    useEffect(() => {
        dispatch(accountAuthInfoPagingAction({ pageIndex: 1, pageSize: 1000 }));
        const loadAccounts = async () => {
            try {
                const response = await Get(ACCOUNTAUTHINFO_API.GET_PAGING, null, {
                    pageIndex: 1,
                    pageSize: 10000
                });
                setFetchedAccounts(getPagedData(response));
            } catch (e) {
                console.error('Error loading accounts:', e);
            }
        };
        const loadPositions = async () => {
            try {
                const res = await Get(POSITION_API.GET_ALL_ACTIVE, null, null);
                setPositions(getArrayData(res));
            } catch (e) {
                console.error('Error loading positions:', e);
            }
        };
        const loadJobGroups = async () => {
            try {
                const res = await Get(JOBGROUP_API.GET_ALL, null, null);
                setJobGroups(getArrayData(res));
            } catch (e) {
                console.error('Error loading job groups:', e);
            }
        };
        const loadAnnualJobs = async () => {
            try {
                const res = await Get(ANNUAL_JOB_API.GET_ALL_ACTIVE, null, null);
                setAnnualJobs(getArrayData(res));
            } catch (e) {
                console.error('Error loading annual jobs:', e);
            }
        };
        loadAccounts();
        loadPositions();
        loadJobGroups();
        loadAnnualJobs();
    }, [dispatch]);

    const accounts = useMemo(() => {
        const arr = [];
        const seen = new Set();
        [...(listAccount || []), ...(fetchedAccounts || [])].forEach(item => {
            if (!item) return;
            const key = item.id || item.Id || item.accountId || item.AccountId || item.username;
            if (key && !seen.has(String(key).toLowerCase())) {
                seen.add(String(key).toLowerCase());
                arr.push(item);
            } else if (!key) {
                arr.push(item);
            }
        });
        return arr;
    }, [listAccount, fetchedAccounts]);

    const accountMap = useMemo(() => {
        const map = new Map();
        accounts.forEach(item => {
            if (!item) return;
            let id = '';
            let username = '';
            let fullName = '';
            let authInfoId = '';
            let positionName = item.positionName || item.PositionName || '';
            let positionId = item.positionId || item.PositionId || '';

            const hasRoles = item.roles || item.Roles;
            if (hasRoles) {
                id = item.id || item.Id;
                username = item.username || item.Username;

                const authInfo = item.accountAuthInfo || item.AccountAuthInfo;
                if (authInfo) {
                    fullName = getFullName(authInfo);
                    authInfoId = authInfo.id || authInfo.Id || '';
                    if (!positionName) positionName = authInfo.positionName || authInfo.PositionName || '';
                    if (!positionId) positionId = authInfo.positionId || authInfo.PositionId || '';
                }
            } else {
                const acc = item.account || item.Account || item.accountResponse || item.AccountResponse;
                id = item.accountId || item.AccountId || acc?.id || acc?.Id;
                username = acc?.username || acc?.Username || item.username || item.Username;
                authInfoId = item.id || item.Id || '';

                fullName = getFullName(item);
                if (acc && !positionName) {
                    positionName = acc.positionName || acc.PositionName || '';
                    positionId = acc.positionId || acc.PositionId || '';
                }
            }

            if (!fullName && username && !isGuid(username)) {
                fullName = username;
            }

            const standardized = {
                id: id ? String(id).trim() : '',
                authInfoId: authInfoId ? String(authInfoId).trim() : '',
                fullName: fullName ? String(fullName).trim() : '',
                username: username ? String(username).trim() : '',
                positionName,
                positionId,
                raw: item
            };

            const keys = [
                standardized.id,
                standardized.authInfoId,
                item.accountId,
                item.AccountId,
                item.id,
                item.Id,
                item.account?.id,
                item.Account?.id,
                item.accountResponse?.id,
                item.AccountResponse?.id,
                standardized.username,
                item.email,
                item.Email
            ];

            keys.forEach(k => {
                if (k) {
                    map.set(String(k).trim().toLowerCase(), standardized);
                }
            });
        });
        return map;
    }, [accounts]);

    const findAccount = (row) => {
        if (!row) return null;
        const uid = (row.userId || '').toString().trim().toLowerCase();
        if (uid && accountMap.has(uid)) return accountMap.get(uid);

        const empName = (row.employeeName || '').toString().trim().toLowerCase();
        if (empName && accountMap.has(empName)) return accountMap.get(empName);

        const uname = (row.username || '').toString().trim().toLowerCase();
        if (uname && accountMap.has(uname)) return accountMap.get(uname);

        return null;
    };

    const positionOptions = useMemo(() => [
        { value: '', label: '-- Tất cả vị trí --' },
        ...positions.map(p => ({ value: p.id, label: p.name }))
    ], [positions]);

    const jobGroupOptions = useMemo(() => [
        { value: '', label: '-- Tất cả nhóm việc --' },
        ...jobGroups.map(jg => ({ value: jg.id, label: jg.name }))
    ], [jobGroups]);

    // Cascading Tab 1: Danh mục theo Nhóm việc
    const filteredAllTasksCategories = useMemo(() => {
        if (!allTasksJobGroupId) return annualJobs;
        return annualJobs.filter(job => job.jobGroupId === Number(allTasksJobGroupId));
    }, [annualJobs, allTasksJobGroupId]);

    const allTasksCategoryOptions = useMemo(() => [
        { value: '', label: '-- Tất cả danh mục --' },
        ...filteredAllTasksCategories.map(cat => ({
            value: cat.id,
            label: cat.name
        }))
    ], [filteredAllTasksCategories]);

    // Cascading Tab 2: Danh mục theo Nhóm việc
    const filteredEvalCategories = useMemo(() => {
        if (!evalJobGroupId) return annualJobs;
        return annualJobs.filter(job => job.jobGroupId === Number(evalJobGroupId));
    }, [annualJobs, evalJobGroupId]);

    const evalCategoryOptions = useMemo(() => [
        { value: '', label: '-- Tất cả danh mục --' },
        ...filteredEvalCategories.map(cat => ({
            value: cat.id,
            label: cat.name
        }))
    ], [filteredEvalCategories]);

    const hasAppKpiC9Role = account => {
        const roles = account?.roles || account?.Roles
            || account?.account?.roles || account?.account?.Roles
            || account?.accountResponse?.roles || account?.accountResponse?.Roles
            || [];
        if (!Array.isArray(roles)) return false;
        return roles.some(r => {
            const roleName = typeof r === 'string' ? r : (r.name || r.Name || '');
            return String(roleName).toUpperCase().includes('APP_KPI_C9');
        });
    };

    const c9Accounts = useMemo(() => {
        return accounts.filter(hasAppKpiC9Role);
    }, [accounts]);

    const filteredAllTasksAccounts = useMemo(() => {
        let list = c9Accounts;
        if (allTasksPositionId) {
            list = list.filter(acc => {
                const posId = acc.positionId || acc.PositionId || acc.position?.id;
                return posId && String(posId).toLowerCase() === String(allTasksPositionId).toLowerCase();
            });
        }
        return list;
    }, [c9Accounts, allTasksPositionId]);

    const filteredEvalAccounts = useMemo(() => {
        let list = c9Accounts;
        if (evalPositionId) {
            list = list.filter(acc => {
                const posId = acc.positionId || acc.PositionId || acc.position?.id;
                return posId && String(posId).toLowerCase() === String(evalPositionId).toLowerCase();
            });
        }
        return list;
    }, [c9Accounts, evalPositionId]);

    const allTasksMemberOptions = useMemo(() => [
        { value: '', label: '-- Tất cả nhân viên --' },
        ...filteredAllTasksAccounts.map(acc => ({
            value: acc.accountId || acc.id || acc.AccountId || acc.Id,
            label: getMemberOptionLabel(acc)
        }))
    ], [filteredAllTasksAccounts]);

    const evalMemberOptions = useMemo(() => [
        { value: '', label: '-- Tất cả nhân viên --' },
        ...filteredEvalAccounts.map(acc => ({
            value: acc.accountId || acc.id || acc.AccountId || acc.Id,
            label: getMemberOptionLabel(acc)
        }))
    ], [filteredEvalAccounts]);

    const allTasksColumnGroups = useMemo(() => {
        const groups = {};
        ALL_TASK_COLUMNS.forEach(col => {
            const group = col.group || 'Khác';
            if (!groups[group]) groups[group] = [];
            groups[group].push(col);
        });
        return groups;
    }, []);

    const evalColumnGroups = useMemo(() => {
        const groups = {};
        EVAL_COLUMNS.forEach(col => {
            const group = col.group || 'Khác';
            if (!groups[group]) groups[group] = [];
            groups[group].push(col);
        });
        return groups;
    }, []);

    const displayEmployeeName = row => {
        if (!row?.userId) return row?.employeeName || '-';
        const account = accountMap.get(row.userId.toLowerCase());
        return account ? (getFullName(account) || getAccountUsername(account)) : (row.employeeName || row.userId);
    };

    // Xuất báo cáo Tab 1: Toàn bộ công việc nhân viên
    const exportAllTasks = async event => {
        if (event) event.preventDefault();
        if (allTasksFromDate && allTasksToDate && allTasksFromDate > allTasksToDate) {
            toast.warning('Từ ngày không được lớn hơn đến ngày.');
            return;
        }

        if (allTasksColumns.length === 0) {
            toast.warning('Vui lòng chọn ít nhất 1 trường để xuất dữ liệu.');
            return;
        }

        const filter = {
            includeAllStatuses: true
        };
        if (allTasksFromDate) filter.fromDate = allTasksFromDate;
        if (allTasksToDate) filter.toDate = allTasksToDate;

        if (allTasksUserId) {
            filter.userIds = [allTasksUserId];
        } else if (allTasksPositionId) {
            const userIdsInPos = filteredAllTasksAccounts.map(a => a.accountId).filter(Boolean);
            if (userIdsInPos.length > 0) {
                filter.userIds = userIdsInPos;
            }
        }

        if (allTasksJobGroupId) {
            filter.jobGroupId = Number(allTasksJobGroupId);
        }

        if (allTasksCategoryId) {
            filter.categoryId = Number(allTasksCategoryId);
        }

        if (allTasksWorkTypeCategory === 'annual') {
            filter.workType = 1;
        } else if (allTasksWorkTypeCategory === 'assigned') {
            filter.workType = 2;
            filter.projectType = 1;
        } else if (allTasksWorkTypeCategory === 'extraordinary') {
            filter.workType = 2;
            filter.projectType = 0;
        }

        if (allTasksStatus !== '') filter.status = Number(allTasksStatus);
        if (allTasksEvaluated !== '') filter.isEvaluated = allTasksEvaluated === 'true';
        if (allTasksReturned !== '') filter.isReturned = allTasksReturned === 'true';

        setIsExportingAllTasks(true);
        try {
            let response = await Get(WORK_RESULT_API.GET_EXPORT_DATA, null, filter);
            let data = getArrayData(response);

            // Fallback to GET_ALL_PAGING if backend was not restarted or returns null
            if (!response || data.length === 0) {
                const pagedRes = await Get(WORK_RESULT_API.GET_ALL_PAGING, null, {
                    ...filter,
                    pageIndex: 1,
                    pageSize: 100000
                });
                const pagedData = getArrayData(pagedRes);
                if (pagedData.length > 0) {
                    data = pagedData;
                }
            }

            if (data.length === 0) {
                toast.info('Không có dữ liệu phù hợp với điều kiện lọc để xuất.');
                return;
            }

            const XLSX = await import('xlsx');
            const excelRows = data.map((row, index) => {
                const acc = findAccount(row);
                const username = acc?.username || getAccountUsername(acc?.raw || acc) || (!isGuid(row.employeeName) ? row.employeeName : '') || (!isGuid(row.username) ? row.username : '') || '';
                const fullName = acc?.fullName || getFullName(acc?.raw || acc) || (!isGuid(row.employeeName) ? row.employeeName : '') || row.fullName || (username || '-');
                const posName = acc?.positionName || acc?.PositionName || acc?.position?.name || positions.find(p => p.id === (acc?.positionId || acc?.PositionId))?.name || row.positionName || '-';
                const jgName = row.jobGroupName || jobGroups.find(jg => jg.id === row.jobGroupId)?.name || '-';

                const rowObj = {};
                if (allTasksColumns.includes('stt')) rowObj['STT'] = index + 1;
                if (allTasksColumns.includes('username')) rowObj['Tài khoản'] = username;
                if (allTasksColumns.includes('fullName')) rowObj['Họ và tên'] = fullName;
                if (allTasksColumns.includes('position')) rowObj['Vị trí / Chức vụ'] = posName;
                if (allTasksColumns.includes('workType')) rowObj['Loại công việc'] = getWorkTypeName(row.workType, row.projectType);
                if (allTasksColumns.includes('jobGroup')) rowObj['Nhóm công việc'] = jgName;
                if (allTasksColumns.includes('category')) rowObj['Danh mục công việc'] = row.categoryName || '-';
                if (allTasksColumns.includes('workName')) rowObj['Tên công việc'] = row.workName || '';
                if (allTasksColumns.includes('description')) rowObj['Mô tả / Yêu cầu'] = row.description || '';
                if (allTasksColumns.includes('status')) rowObj['Trạng thái'] = getStatusName(row.status, row.workType);
                if (allTasksColumns.includes('returned')) rowObj['Hoàn trả'] = row.isReturned ? '⚠️ Bị hoàn trả' : 'Bình thường';
                if (allTasksColumns.includes('returnComment')) rowObj['Lý do hoàn trả'] = row.returnComment || '';
                if (allTasksColumns.includes('registeredKPI')) rowObj['Khối lượng ĐK'] = row.registeredKPI != null ? row.registeredKPI : '';
                if (allTasksColumns.includes('currentKPI')) rowObj['Khối lượng đạt'] = row.currentKPI != null ? row.currentKPI : '';
                if (allTasksColumns.includes('unit')) rowObj['Đơn vị tính'] = row.unit || '';
                if (allTasksColumns.includes('createdDate')) rowObj['Ngày giao / Tạo'] = fmtDate(row.createdDate);
                if (allTasksColumns.includes('startDate')) rowObj['Ngày bắt đầu'] = fmtDate(row.startDate);
                if (allTasksColumns.includes('endDate')) rowObj['Hạn hoàn thành'] = fmtDate(row.endDate);
                if (allTasksColumns.includes('actualEndDate')) rowObj['Ngày HT thực tế'] = fmtDate(row.actualEndDate);
                if (allTasksColumns.includes('baseScore')) rowObj['Điểm cơ sở / Trọng số'] = row.baseScore != null ? row.baseScore : '';
                if (allTasksColumns.includes('evaluationPercent')) rowObj['Tỷ lệ đánh giá'] = row.evaluationPercent == null ? 'Chưa đánh giá' : `${row.evaluationPercent}%`;
                if (allTasksColumns.includes('actualScore')) rowObj['Điểm thực nhận'] = row.actualScore != null ? row.actualScore : '';
                if (allTasksColumns.includes('evaluatedBy')) rowObj['Người đánh giá'] = row.evaluatedBy || '-';
                if (allTasksColumns.includes('evaluatedAt')) rowObj['Ngày đánh giá'] = fmtDate(row.evaluatedAt);
                if (allTasksColumns.includes('evaluationNote')) rowObj['Ghi chú đánh giá'] = row.evaluationNote || '';

                return rowObj;
            });

            const cols = ALL_TASK_COLUMNS
                .filter(c => allTasksColumns.includes(c.id))
                .map(c => ({ wch: c.defaultWidth }));

            const worksheet = XLSX.utils.json_to_sheet(excelRows);
            worksheet['!cols'] = cols;
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Toàn bộ công việc');

            const now = new Date();
            const pad = value => String(value).padStart(2, '0');
            const fileName = `Bao_cao_toan_bo_cong_viec_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}.xlsx`;
            XLSX.writeFile(workbook, fileName);
            toast.success(`Đã xuất thành công ${data.length} công việc (${allTasksColumns.length} cột).`);
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setIsExportingAllTasks(false);
        }
    };

    // Xuất báo cáo Tab 2: Đánh giá kết quả
    const exportEvalReport = async event => {
        if (event) event.preventDefault();
        if (evalFromDate && evalToDate && evalFromDate > evalToDate) {
            toast.warning('Từ ngày không được lớn hơn đến ngày.');
            return;
        }

        if (evalColumns.length === 0) {
            toast.warning('Vui lòng chọn ít nhất 1 trường để xuất dữ liệu.');
            return;
        }

        const filter = {
            includeAllStatuses: false
        };
        if (evalFromDate) filter.fromDate = evalFromDate;
        if (evalToDate) filter.toDate = evalToDate;

        if (evalUserId) {
            filter.userIds = [evalUserId];
        } else if (evalPositionId) {
            const userIdsInPos = filteredEvalAccounts.map(a => a.accountId).filter(Boolean);
            if (userIdsInPos.length > 0) {
                filter.userIds = userIdsInPos;
            }
        }

        if (evalJobGroupId) {
            filter.jobGroupId = Number(evalJobGroupId);
        }

        if (evalCategoryId) {
            filter.categoryId = Number(evalCategoryId);
        }

        if (evalWorkType !== '') filter.workType = Number(evalWorkType);
        if (evalStatus !== '') filter.status = Number(evalStatus);
        if (evalEvaluated !== '') filter.isEvaluated = evalEvaluated === 'true';
        if (evalReturned !== '') filter.isReturned = evalReturned === 'true';

        setIsExportingEval(true);
        try {
            let response = await Get(WORK_RESULT_API.GET_EXPORT_DATA, null, filter);
            let data = getArrayData(response);

            // Fallback to GET_ALL_PAGING if backend was not restarted or returns null
            if (!response || data.length === 0) {
                const pagedRes = await Get(WORK_RESULT_API.GET_ALL_PAGING, null, {
                    ...filter,
                    pageIndex: 1,
                    pageSize: 100000
                });
                const pagedData = getArrayData(pagedRes);
                if (pagedData.length > 0) {
                    data = pagedData;
                }
            }

            if (data.length === 0) {
                toast.info('Không có dữ liệu phù hợp với điều kiện lọc để xuất.');
                return;
            }

            const XLSX = await import('xlsx');
            const excelRows = data.map((row, index) => {
                const acc = findAccount(row);
                const username = acc?.username || getAccountUsername(acc?.raw || acc) || (!isGuid(row.employeeName) ? row.employeeName : '') || (!isGuid(row.username) ? row.username : '') || '';
                const fullName = acc?.fullName || getFullName(acc?.raw || acc) || (!isGuid(row.employeeName) ? row.employeeName : '') || row.fullName || (username || '-');
                const posName = acc?.positionName || acc?.PositionName || acc?.position?.name || positions.find(p => p.id === (acc?.positionId || acc?.PositionId))?.name || row.positionName || '-';
                const jgName = row.jobGroupName || jobGroups.find(jg => jg.id === row.jobGroupId)?.name || '-';

                const rowObj = {};
                if (evalColumns.includes('stt')) rowObj['STT'] = index + 1;
                if (evalColumns.includes('username')) rowObj['Tài khoản'] = username;
                if (evalColumns.includes('fullName')) rowObj['Họ và tên'] = fullName;
                if (evalColumns.includes('position')) rowObj['Vị trí / Chức vụ'] = posName;
                if (evalColumns.includes('workType')) rowObj['Loại công việc'] = row.workType === 1 ? 'Thường niên' : 'Dự án / Giao việc';
                if (evalColumns.includes('jobGroup')) rowObj['Nhóm công việc'] = jgName;
                if (evalColumns.includes('category')) rowObj['Danh mục công việc'] = row.categoryName || '-';
                if (evalColumns.includes('workName')) rowObj['Tên công việc'] = row.workName || '';
                if (evalColumns.includes('status')) rowObj['Trạng thái'] = row.status === 3 ? 'Hoàn thành' : 'Quá hạn';
                if (evalColumns.includes('endDate')) rowObj['Hạn hoàn thành'] = fmtDate(row.endDate);
                if (evalColumns.includes('baseScore')) rowObj['Điểm trước đánh giá'] = row.baseScore ?? '';
                if (evalColumns.includes('evaluationPercent')) rowObj['Tỷ lệ đánh giá'] = row.evaluationPercent == null ? 'Chưa đánh giá' : `${row.evaluationPercent}%`;
                if (evalColumns.includes('actualScore')) rowObj['Điểm thực nhận'] = row.actualScore ?? '';
                if (evalColumns.includes('evaluatedBy')) rowObj['Đánh giá gần nhất'] = row.evaluatedBy
                    ? `${row.evaluatedBy}${row.evaluatedAt ? ` - ${fmtDate(row.evaluatedAt)}` : ''}`
                    : '-';

                return rowObj;
            });

            const cols = EVAL_COLUMNS
                .filter(c => evalColumns.includes(c.id))
                .map(c => ({ wch: c.defaultWidth }));

            const worksheet = XLSX.utils.json_to_sheet(excelRows);
            worksheet['!cols'] = cols;
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Kết quả đánh giá');

            const now = new Date();
            const pad = value => String(value).padStart(2, '0');
            const fileName = `Ket_qua_danh_gia_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}.xlsx`;
            XLSX.writeFile(workbook, fileName);
            toast.success(`Đã xuất thành công ${data.length} công việc đánh giá (${evalColumns.length} cột).`);
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setIsExportingEval(false);
        }
    };

    return (
        <div className="container-fluid">
            <style>{`
                .report-filter-grid {
                    display: grid;
                    grid-template-columns: repeat(5, minmax(0, 1fr));
                    gap: 12px 16px;
                }
                @media (max-width: 1200px) {
                    .report-filter-grid {
                        grid-template-columns: repeat(3, minmax(0, 1fr));
                    }
                }
                @media (max-width: 768px) {
                    .report-filter-grid {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }
                }
                @media (max-width: 576px) {
                    .report-filter-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
            <div className="row">
                <div className="col-lg-12">
                    <Card>
                        <Card.Header>
                            <h3 className="card-title">Xuất báo cáo</h3>
                        </Card.Header>
                        <Card.Body>
                            <Tabs defaultActiveKey="all-tasks" id="report-export-tabs" className="mb-3">
                                {/* TAB 1: TOÀN BỘ CÔNG VIỆC NHÂN VIÊN */}
                                <Tab eventKey="all-tasks" title="📊 Báo cáo toàn bộ công việc nhân viên">
                                    <div className="alert alert-light border mb-3">
                                        <div className="d-flex flex-wrap align-items-center justify-content-between">
                                            <div>
                                                <strong>Xuất toàn bộ công việc của nhân viên:</strong>
                                                <div className="text-muted mt-1" style={{ fontSize: '0.9rem' }}>
                                                    File Excel chứa toàn bộ các công việc ở tất cả các trạng thái (Chưa thực hiện, Đang làm, Hoàn thành, Quá hạn, Không hoàn thành) thuộc cả 3 loại (Thường niên, Giao việc, Đột xuất).
                                                </div>
                                            </div>
                                            <div className="mt-2 mt-md-0">
                                                <Button
                                                    size="sm"
                                                    variant={showAllTasksColConfig ? 'info' : 'outline-info'}
                                                    onClick={() => setShowAllTasksColConfig(!showAllTasksColConfig)}
                                                >
                                                    <i className="fas fa-sliders-h mr-1" />
                                                    {showAllTasksColConfig ? 'Ẩn chọn trường xuất' : `Tùy chọn trường xuất (${allTasksColumns.length}/${ALL_TASK_COLUMNS.length})`}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    {showAllTasksColConfig && (
                                        <Card className="card-outline card-info mb-3">
                                            <Card.Header className="p-2 d-flex justify-content-between align-items-center">
                                                <h6 className="card-title mb-0 font-weight-bold" style={{ fontSize: '0.95rem' }}>
                                                    <i className="fas fa-columns mr-1 text-info" />
                                                    Chọn các trường xuất file Excel (Mặc định: Tất cả | Đang chọn: {allTasksColumns.length}/{ALL_TASK_COLUMNS.length})
                                                </h6>
                                                <div>
                                                    <Button size="sm" variant="outline-primary" className="mr-1 py-0 px-2" onClick={() => setAllTasksColumns(ALL_TASK_COLUMNS.map(c => c.id))}>
                                                        <i className="fas fa-check-square mr-1" /> Chọn tất cả
                                                    </Button>
                                                    <Button size="sm" variant="outline-danger" className="py-0 px-2" onClick={() => setAllTasksColumns([])}>
                                                        <i className="fas fa-square mr-1" /> Bỏ chọn tất cả
                                                    </Button>
                                                </div>
                                            </Card.Header>
                                            <Card.Body className="p-3">
                                                <Row>
                                                    {Object.entries(allTasksColumnGroups).map(([groupName, cols]) => (
                                                        <Col key={groupName} md={4} sm={6} className="mb-3">
                                                            <div className="font-weight-bold text-primary border-bottom pb-1 mb-2" style={{ fontSize: '0.85rem' }}>
                                                                {groupName}
                                                            </div>
                                                            {cols.map(col => (
                                                                <Form.Check
                                                                    key={col.id}
                                                                    type="checkbox"
                                                                    id={`all-tasks-col-${col.id}`}
                                                                    label={col.label}
                                                                    checked={allTasksColumns.includes(col.id)}
                                                                    onChange={() => setAllTasksColumns(prev =>
                                                                        prev.includes(col.id) ? prev.filter(id => id !== col.id) : [...prev, col.id]
                                                                    )}
                                                                    className="mb-1"
                                                                    style={{ fontSize: '0.88rem' }}
                                                                />
                                                            ))}
                                                        </Col>
                                                    ))}
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    )}

                                    <Card className="mb-3 bg-light">
                                        <Card.Body className="p-3">
                                            <Form onSubmit={exportAllTasks}>
                                                <div className="report-filter-grid mb-3">
                                                    <div>
                                                        <Form.Label className="mb-1">Từ ngày</Form.Label>
                                                        <Form.Control type="date" value={allTasksFromDate} onChange={e => setAllTasksFromDate(e.target.value)} style={{ height: '38px' }} />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Đến ngày</Form.Label>
                                                        <Form.Control type="date" value={allTasksToDate} onChange={e => setAllTasksToDate(e.target.value)} style={{ height: '38px' }} />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Vị trí / Chức vụ</Form.Label>
                                                        <Select
                                                            options={positionOptions}
                                                            value={positionOptions.find(opt => opt.value === allTasksPositionId) || positionOptions[0]}
                                                            onChange={opt => {
                                                                setAllTasksPositionId(opt ? opt.value : '');
                                                                setAllTasksUserId('');
                                                            }}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả vị trí --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Thành viên / Nhân viên</Form.Label>
                                                        <Select
                                                            options={allTasksMemberOptions}
                                                            value={allTasksMemberOptions.find(opt => opt.value === allTasksUserId) || allTasksMemberOptions[0]}
                                                            onChange={opt => setAllTasksUserId(opt ? opt.value : '')}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả nhân viên --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Nhóm công việc</Form.Label>
                                                        <Select
                                                            options={jobGroupOptions}
                                                            value={jobGroupOptions.find(opt => opt.value === allTasksJobGroupId) || jobGroupOptions[0]}
                                                            onChange={opt => {
                                                                setAllTasksJobGroupId(opt ? opt.value : '');
                                                                setAllTasksCategoryId('');
                                                            }}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả nhóm việc --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Danh mục công việc</Form.Label>
                                                        <Select
                                                            options={allTasksCategoryOptions}
                                                            value={allTasksCategoryOptions.find(opt => opt.value === allTasksCategoryId) || allTasksCategoryOptions[0]}
                                                            onChange={opt => setAllTasksCategoryId(opt ? opt.value : '')}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả danh mục --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Loại công việc</Form.Label>
                                                        <Select
                                                            options={ALL_TASKS_WORK_TYPE_OPTIONS}
                                                            value={ALL_TASKS_WORK_TYPE_OPTIONS.find(opt => opt.value === allTasksWorkTypeCategory) || ALL_TASKS_WORK_TYPE_OPTIONS[0]}
                                                            onChange={opt => setAllTasksWorkTypeCategory(opt ? opt.value : '')}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả loại --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Trạng thái</Form.Label>
                                                        <Select
                                                            options={ALL_TASKS_STATUS_OPTIONS}
                                                            value={ALL_TASKS_STATUS_OPTIONS.find(opt => opt.value === allTasksStatus) || ALL_TASKS_STATUS_OPTIONS[0]}
                                                            onChange={opt => setAllTasksStatus(opt ? opt.value : '')}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả trạng thái --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Đánh giá</Form.Label>
                                                        <Select
                                                            options={EVALUATED_OPTIONS}
                                                            value={EVALUATED_OPTIONS.find(opt => opt.value === allTasksEvaluated) || EVALUATED_OPTIONS[0]}
                                                            onChange={opt => setAllTasksEvaluated(opt ? opt.value : '')}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Hoàn trả</Form.Label>
                                                        <Select
                                                            options={RETURNED_OPTIONS}
                                                            value={RETURNED_OPTIONS.find(opt => opt.value === allTasksReturned) || RETURNED_OPTIONS[0]}
                                                            onChange={opt => setAllTasksReturned(opt ? opt.value : '')}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả --"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="d-flex justify-content-end pt-1">
                                                    <Button type="submit" variant="success" disabled={isExportingAllTasks} style={{ minWidth: '180px', height: '38px' }}>
                                                        <i className="fas fa-file-excel mr-1" />
                                                        {isExportingAllTasks ? 'Đang xuất file...' : 'Xuất file Excel'}
                                                    </Button>
                                                </div>
                                            </Form>
                                        </Card.Body>
                                    </Card>
                                </Tab>

                                {/* TAB 2: ĐÁNH GIÁ KẾT QUẢ */}
                                <Tab eventKey="evaluation" title="🎯 Báo cáo đánh giá kết quả">
                                    <div className="alert alert-light border mb-3">
                                        <div className="d-flex flex-wrap align-items-center justify-content-between">
                                            <div>
                                                <strong>Xuất báo cáo đánh giá kết quả:</strong>
                                                <div className="text-muted mt-1" style={{ fontSize: '0.9rem' }}>
                                                    File Excel chứa danh sách các công việc đã Hoàn thành hoặc Quá hạn phục vụ công tác chấm điểm và tổng hợp đánh giá kết quả công việc.
                                                </div>
                                            </div>
                                            <div className="mt-2 mt-md-0">
                                                <Button
                                                    size="sm"
                                                    variant={showEvalColConfig ? 'info' : 'outline-info'}
                                                    onClick={() => setShowEvalColConfig(!showEvalColConfig)}
                                                >
                                                    <i className="fas fa-sliders-h mr-1" />
                                                    {showEvalColConfig ? 'Ẩn chọn trường xuất' : `Tùy chọn trường xuất (${evalColumns.length}/${EVAL_COLUMNS.length})`}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    {showEvalColConfig && (
                                        <Card className="card-outline card-info mb-3">
                                            <Card.Header className="p-2 d-flex justify-content-between align-items-center">
                                                <h6 className="card-title mb-0 font-weight-bold" style={{ fontSize: '0.95rem' }}>
                                                    <i className="fas fa-columns mr-1 text-info" />
                                                    Chọn các trường xuất file Excel (Mặc định: Tất cả | Đang chọn: {evalColumns.length}/{EVAL_COLUMNS.length})
                                                </h6>
                                                <div>
                                                    <Button size="sm" variant="outline-primary" className="mr-1 py-0 px-2" onClick={() => setEvalColumns(EVAL_COLUMNS.map(c => c.id))}>
                                                        <i className="fas fa-check-square mr-1" /> Chọn tất cả
                                                    </Button>
                                                    <Button size="sm" variant="outline-danger" className="py-0 px-2" onClick={() => setEvalColumns([])}>
                                                        <i className="fas fa-square mr-1" /> Bỏ chọn tất cả
                                                    </Button>
                                                </div>
                                            </Card.Header>
                                            <Card.Body className="p-3">
                                                <Row>
                                                    {Object.entries(evalColumnGroups).map(([groupName, cols]) => (
                                                        <Col key={groupName} md={4} sm={6} className="mb-3">
                                                            <div className="font-weight-bold text-primary border-bottom pb-1 mb-2" style={{ fontSize: '0.85rem' }}>
                                                                {groupName}
                                                            </div>
                                                            {cols.map(col => (
                                                                <Form.Check
                                                                    key={col.id}
                                                                    type="checkbox"
                                                                    id={`eval-col-${col.id}`}
                                                                    label={col.label}
                                                                    checked={evalColumns.includes(col.id)}
                                                                    onChange={() => setEvalColumns(prev =>
                                                                        prev.includes(col.id) ? prev.filter(id => id !== col.id) : [...prev, col.id]
                                                                    )}
                                                                    className="mb-1"
                                                                    style={{ fontSize: '0.88rem' }}
                                                                />
                                                            ))}
                                                        </Col>
                                                    ))}
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    )}

                                    <Card className="mb-3 bg-light">
                                        <Card.Body className="p-3">
                                            <Form onSubmit={exportEvalReport}>
                                                <div className="report-filter-grid mb-3">
                                                    <div>
                                                        <Form.Label className="mb-1">Từ ngày</Form.Label>
                                                        <Form.Control type="date" value={evalFromDate} onChange={e => setEvalFromDate(e.target.value)} style={{ height: '38px' }} />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Đến ngày</Form.Label>
                                                        <Form.Control type="date" value={evalToDate} onChange={e => setEvalToDate(e.target.value)} style={{ height: '38px' }} />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Vị trí / Chức vụ</Form.Label>
                                                        <Select
                                                            options={positionOptions}
                                                            value={positionOptions.find(opt => opt.value === evalPositionId) || positionOptions[0]}
                                                            onChange={opt => {
                                                                setEvalPositionId(opt ? opt.value : '');
                                                                setEvalUserId('');
                                                            }}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả vị trí --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Thành viên / Nhân viên</Form.Label>
                                                        <Select
                                                            options={evalMemberOptions}
                                                            value={evalMemberOptions.find(opt => opt.value === evalUserId) || evalMemberOptions[0]}
                                                            onChange={opt => setEvalUserId(opt ? opt.value : '')}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả nhân viên --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Nhóm công việc</Form.Label>
                                                        <Select
                                                            options={jobGroupOptions}
                                                            value={jobGroupOptions.find(opt => opt.value === evalJobGroupId) || jobGroupOptions[0]}
                                                            onChange={opt => {
                                                                setEvalJobGroupId(opt ? opt.value : '');
                                                                setEvalCategoryId('');
                                                            }}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả nhóm việc --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Danh mục công việc</Form.Label>
                                                        <Select
                                                            options={evalCategoryOptions}
                                                            value={evalCategoryOptions.find(opt => opt.value === evalCategoryId) || evalCategoryOptions[0]}
                                                            onChange={opt => setEvalCategoryId(opt ? opt.value : '')}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả danh mục --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Loại công việc</Form.Label>
                                                        <Select
                                                            options={EVAL_WORK_TYPE_OPTIONS}
                                                            value={EVAL_WORK_TYPE_OPTIONS.find(opt => opt.value === evalWorkType) || EVAL_WORK_TYPE_OPTIONS[0]}
                                                            onChange={opt => setEvalWorkType(opt ? opt.value : '')}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả loại --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Trạng thái</Form.Label>
                                                        <Select
                                                            options={EVAL_STATUS_OPTIONS}
                                                            value={EVAL_STATUS_OPTIONS.find(opt => opt.value === evalStatus) || EVAL_STATUS_OPTIONS[0]}
                                                            onChange={opt => setEvalStatus(opt ? opt.value : '')}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả trạng thái --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Đánh giá</Form.Label>
                                                        <Select
                                                            options={EVALUATED_OPTIONS}
                                                            value={EVALUATED_OPTIONS.find(opt => opt.value === evalEvaluated) || EVALUATED_OPTIONS[0]}
                                                            onChange={opt => setEvalEvaluated(opt ? opt.value : '')}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả --"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label className="mb-1">Hoàn trả</Form.Label>
                                                        <Select
                                                            options={RETURNED_OPTIONS}
                                                            value={RETURNED_OPTIONS.find(opt => opt.value === evalReturned) || RETURNED_OPTIONS[0]}
                                                            onChange={opt => setEvalReturned(opt ? opt.value : '')}
                                                            isSearchable
                                                            styles={selectCustomStyles}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            placeholder="-- Tất cả --"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="d-flex justify-content-end pt-1">
                                                    <Button type="submit" variant="success" disabled={isExportingEval} style={{ minWidth: '180px', height: '38px' }}>
                                                        <i className="fas fa-file-excel mr-1" />
                                                        {isExportingEval ? 'Đang xuất file...' : 'Xuất file Excel'}
                                                    </Button>
                                                </div>
                                            </Form>
                                        </Card.Body>
                                    </Card>
                                </Tab>
                            </Tabs>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ReportExport;
